"use client";

import { useCallback, useState } from "react";

/**
 * Collapsible sidebar groups stay closed.
 * The group that contains the current page opens on its own.
 * A click can open or close a group until the current page changes.
 */
export function useSidebarGroupExpansion(activeGroupIds: readonly string[]) {
  const activeKey = activeGroupIds.join("\0");
  const [overrideById, setOverrideById] = useState<Record<string, boolean>>({});
  const [overrideKey, setOverrideKey] = useState(activeKey);

  if (overrideKey !== activeKey) {
    setOverrideKey(activeKey);
    setOverrideById({});
  }

  const isExpanded = useCallback(
    (groupId: string) => {
      if (Object.prototype.hasOwnProperty.call(overrideById, groupId)) {
        return overrideById[groupId] === true;
      }
      return activeGroupIds.includes(groupId);
    },
    [activeGroupIds, overrideById],
  );

  const toggleGroup = useCallback(
    (groupId: string) => {
      setOverrideById((current) => {
        const open = Object.prototype.hasOwnProperty.call(current, groupId)
          ? current[groupId] === true
          : activeGroupIds.includes(groupId);
        return { ...current, [groupId]: !open };
      });
    },
    [activeGroupIds],
  );

  return { isExpanded, toggleGroup };
}
